/**
 * Subscription and billing models for the Stratum platform.
 *
 * These interfaces match the API response shapes for:
 *  - GET /api/subscription-plans/public
 *  - GET /api/organizations/:orgId/billing
 *  - GET /api/organizations/:orgId/quota-dashboard
 *  - POST /api/billing/checkout
 *  - POST /api/billing/portal
 */

// ---------------------------------------------------------------------------
// Plan / pricing models
// ---------------------------------------------------------------------------

/** Pricing for a single billing cycle */
export interface PlanPricing {
  /** Price in cents (e.g. 2900 = $29.00) */
  amountCents: number;
  /** ISO 4217 currency code */
  currency: string;
  /** Discount percentage for annual billing (annual only) */
  discountPercent?: number;
  /** Pre-discount amount in cents — present when a promotional price is active */
  originalAmountCents?: number;
}

/** Pricing container returned from the public plans API */
export interface PlanPricingContainer {
  monthly: PlanPricing | null;
  annual: PlanPricing | null;
}

/** A single feature entry within a subscription plan */
export interface PlanFeature {
  /** Grouping category, e.g. 'core' | 'analytics' | 'media' | 'content' */
  category: string;
  /** Machine-readable key, e.g. 'media.video' | 'analytics_dashboard' */
  key: string;
  /** Human-readable label */
  label: string;
  /** Whether this feature is enabled on the plan */
  enabled: boolean;
  /** Optional short qualifier shown next to the label, e.g. 'Up to 10' or 'Unlimited' */
  limitLabel?: string;
}

/** Plan usage limits */
export interface PlanLimits {
  maxMembers: number | null;
  maxPosts: number | null;
  maxVideoMinutes: number | null;
  /** Monthly viewer watch-minutes allowed; -1 = unlimited, 0 = not included */
  maxDeliveryMinutes: number | null;
  /** Storage cap in MB — matches API field `maxAudioMB` */
  maxAudioMB: number | null;
  /** Storage cap in MB — matches API field `maxImageMB` */
  maxImageMB: number | null;
  /** Storage cap in MB — matches API field `maxDocumentMB` */
  maxDocumentMB: number | null;
  maxPostSeries: number | null;
  maxVideoSeries: number | null;
  maxAudioSeries: number | null;
  maxImageGalleries: number | null;
}

/**
 * Full subscription plan as returned by GET /api/subscription-plans/public.
 * `pricing: null` means "Contact sales" (Enterprise).
 * `limit: 0` within PlanLimits means the feature is not available on this plan.
 * `limit: null` means unlimited.
 */
export interface SubscriptionPlan {
  name: string;
  slug: string;
  description: string;
  displayOrder: number;
  isActive: boolean;
  isFeatured: boolean;
  badge: string | null;
  pricing: PlanPricingContainer | null;
  limits: PlanLimits | null;
  features: PlanFeature[] | null;
}

// ---------------------------------------------------------------------------
// Billing status (per org)
// ---------------------------------------------------------------------------

export interface BillingStatus {
  planSlug: string;
  planName: string;
  status: 'active' | 'past_due' | 'canceled' | 'trialing' | 'incomplete';
  billingCycle: 'monthly' | 'annual' | 'free';
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

// ---------------------------------------------------------------------------
// Quota / usage models
// ---------------------------------------------------------------------------

/**
 * A single quota dimension (storage bucket or count type).
 * - `limit: null` → unlimited
 * - `limit: 0`    → not available on this plan
 */
export interface QuotaDimension {
  used: number;
  limit: number | null;
  unit: string;
  pct: number | null;
}

/** Per-billing-period delivery (viewer watch-minutes) usage. */
export interface DeliveryUsage {
  used: number;
  /** null = unlimited (Enterprise) */
  limit: number | null;
  unit: 'minutes';
  /** null when limit is null */
  pct: number | null;
  billingPeriodStart: string | null;
  billingPeriodEnd: string | null;
}

export interface QuotaStorage {
  video: QuotaDimension;
  /** Monthly viewer delivery minutes — separate from upload storage */
  delivery: DeliveryUsage;
  audio: QuotaDimension;
  image: QuotaDimension;
  document: QuotaDimension;
}

export interface QuotaCounts {
  posts: QuotaDimension;
  members: QuotaDimension;
  postSeries: QuotaDimension;
  videoSeries: QuotaDimension;
  audioSeries: QuotaDimension;
  imageGalleries: QuotaDimension;
}

export interface QuotaDashboard {
  plan: {
    name: string;
    slug: string;
    status: string;
    billingCycle: string;
  };
  storage: QuotaStorage;
  counts: QuotaCounts;
  warnings: string[];
  billingPeriod: { start: string | null; end: string | null };
}

// ---------------------------------------------------------------------------
// Org-level subscription snapshot (embedded on Organization model)
// ---------------------------------------------------------------------------

/**
 * Lightweight subscription summary stored on the Organization document.
 * Use BillingStatus for full Stripe state.
 */
export interface OrgSubscription {
  planSlug: string;
  status: string;
  renewsAt?: string;
  canceledAt?: string;
}

// ---------------------------------------------------------------------------
// Invoice history models (GET /api/billing/invoices/:orgId)
// ---------------------------------------------------------------------------

export interface InvoiceLine {
  description: string | null;
  /** Amount in cents */
  amount: number;
  currency: string;
}

export interface Invoice {
  id: string;
  number: string | null;
  status: 'paid' | 'open' | 'void' | 'uncollectible' | null;
  /** Amount due in cents */
  amountDue: number;
  /** Amount paid in cents */
  amountPaid: number;
  currency: string;
  periodStart: string | null;
  periodEnd: string | null;
  created: string;
  /** null for draft or void invoices */
  hostedInvoiceUrl: string | null;
  /** null for draft or void invoices */
  invoicePdf: string | null;
  lines: InvoiceLine[];
}

export interface InvoiceHistoryPage {
  invoices: Invoice[];
  hasMore: boolean;
  nextCursor: string | null;
}

export type InvoiceStatusFilter = 'all' | 'paid' | 'open' | 'void' | 'uncollectible';
